import { FC, useState, memo, useEffect, ChangeEvent } from 'react';
import { useNavigate } from 'react-router';
import { useForm, SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { styled } from 'styled-components';

import { AUTH_ROUTES } from '@navigation/types';
import { theme } from 'theme';
import {
  useConfirmCodeMutation,
  useResendVerificationCodeMutation,
} from '@redux/queries/auth';
import { verificationCode } from '@utils/constants/schemas';
import {
  TEXT_INPUT_LABELS,
  TEXT_INPUT_PLACEHOLDERS,
} from '@utils/constants/form';
import AuthTitle from '@components/auth/AuthTitle';
import AuthLayout from '@components/layouts/AuthLayout';
import TextInput from '@components/fields/input';
import {
  StyledErrorContainer,
  StyledForm,
  StyledButton,
} from '@components/form/styled';
import { DefaultText } from '@components/text';
import TouchableOpacity from '@components/buttons/TouchableOpacity';

const Content = styled.div`
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 24px;
`;

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const HintText = styled(DefaultText)`
  margin-bottom: 16px;
  color: ${({ theme }) => theme.colors.grey[200]};
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
`;

const MessageText = styled(DefaultText)<{ $isError?: boolean }>`
  color: ${({ theme, $isError }) =>
    $isError ? theme.colors.red[500] : theme.colors.green[500]};
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
`;

const ResendButton = styled(TouchableOpacity)`
  align-self: center;
  margin-top: 4px;
  color: ${({ theme }) => theme.colors.purple[300]};
  text-decoration: none;
  font-size: ${({ theme }) => theme.fonts.fontSize.base};

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;

type Message = {
  text?: string;
  type?: 'success' | 'error';
} | null;

type ConfirmEmailState = {
  verificationCode: string;
};

const defaultValues: ConfirmEmailState = {
  verificationCode: '',
};

const ConfirmEmail: FC = () => {
  const [message, setMessage] = useState<Message>(null);
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ConfirmEmailState>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(
      yup.object().shape({
        verificationCode,
      }),
    ),
  });
  const [confirmCode, { isLoading, isError, isSuccess }] =
    useConfirmCodeMutation();
  const [
    resendVerificationCode,
    {
      isLoading: isResendLoading,
      isSuccess: isResendSuccess,
      isError: isResendError,
    },
  ] = useResendVerificationCodeMutation();

  const onSubmit: SubmitHandler<ConfirmEmailState> = (data) => {
    confirmCode({ code: Number(data.verificationCode) });

    reset({}, { keepValues: true, keepDirty: false });
  };

  useEffect(() => {
    if (isSuccess) {
      navigate(AUTH_ROUTES.ONBOARDING, { replace: true });
    }
  }, [isSuccess, navigate]);

  useEffect(() => {
    if (isResendSuccess) {
      setMessage({
        text: 'Code has been successfully resent. Check your email to get the code.',
        type: 'success',
      });
    }
  }, [isResendSuccess, setMessage]);

  useEffect(() => {
    if (isResendError) {
      setMessage({ text: 'Please try again later', type: 'error' });
    }
  }, [isResendError, setMessage]);

  useEffect(() => {
    if (isError) {
      setMessage({ text: 'Invalid data', type: 'error' });
    }
  }, [isError, setMessage]);

  return (
    <AuthLayout>
      <Content>
        <AuthTitle title='Check your email' />
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          <FormContainer>
            <TextInput
              placeholder={TEXT_INPUT_PLACEHOLDERS.VERIFICATION_CODE}
              label={TEXT_INPUT_LABELS.VERIFICATION_CODE}
              errorText={errors.verificationCode?.message}
              {...register('verificationCode')}
              value={watch('verificationCode')}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                clearErrors('verificationCode');
                setValue('verificationCode', e.target.value);

                if (message) {
                  setMessage(null);
                }
              }}
              disabled={isLoading || isResendLoading}
            />

            <StyledErrorContainer>
              {!!message && <MessageText>{message.text}</MessageText>}
            </StyledErrorContainer>

            <HintText>
              If you can't see the email, check other places it might be, like
              your junk, spam, social and other folders.
            </HintText>

            <StyledButton
              disabled={
                isLoading || isResendLoading || !watch('verificationCode')
              }
              isLoading={isLoading || isResendLoading}
              type='submit'
              textStyle={{ fontSize: theme.fonts.fontSize.xl }}
            >
              Verify
            </StyledButton>
          </FormContainer>
        </StyledForm>

        <ResendButton
          disabled={isLoading || isResendLoading}
          onClick={() => resendVerificationCode()}
        >
          Didn't receive your code?
        </ResendButton>
      </Content>
    </AuthLayout>
  );
};

export default memo(ConfirmEmail);
